import React, { useState, useEffect, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { TargetTextProps } from '../types';
import { rewriteText, RewriteStyle, getIdeaOfTheDay, synthesizeSpeech, TTSVoice } from '../services/api';

// Cache for storing audio URLs to avoid re-requesting
const ttsAudioCache = new Map<string, string>();

// TTS voice options (matching server)
const TTS_VOICES = [
  { id: 'en-GB-SoniaNeural' as TTSVoice, name: 'Sonia', accent: 'British', gender: 'female' },
  { id: 'en-GB-RyanNeural' as TTSVoice, name: 'Ryan', accent: 'British', gender: 'male' },
  { id: 'en-US-JennyNeural' as TTSVoice, name: 'Jenny', accent: 'American', gender: 'female' },
  { id: 'en-US-GuyNeural' as TTSVoice, name: 'Guy', accent: 'American', gender: 'male' },
];

// Character limit based on Azure Speech pronunciation assessment limits
const CHARACTER_LIMIT = 350;

// Rewrite style options
const REWRITE_STYLES: { value: RewriteStyle; label: string; description: string }[] = [
  { value: 'business', label: 'Business', description: 'Professional & formal' },
  { value: 'original', label: 'Polished', description: 'Fix errors only' },
  { value: 'informal', label: 'Casual', description: 'Natural & friendly' },
];

// Split text into parts based on sentence boundaries
const splitTextIntoParts = (text: string): string[] => {
  if (text.length <= CHARACTER_LIMIT) {
    return [text];
  }

  const sentences = text.match(/[^.!?]+[.!?]+\s*/g) || [text];
  const parts: string[] = [];
  let currentPart = '';

  for (const sentence of sentences) {
    const trimmedSentence = sentence.trim();

    // If adding this sentence would exceed the limit
    if ((currentPart + ' ' + trimmedSentence).trim().length > CHARACTER_LIMIT) {
      // If current part is not empty, save it
      if (currentPart.trim()) {
        parts.push(currentPart.trim());
      }

      // If a single sentence is longer than the limit, split by words
      if (trimmedSentence.length > CHARACTER_LIMIT) {
        const words = trimmedSentence.split(/\s+/);
        let wordPart = '';

        for (const word of words) {
          if ((wordPart + ' ' + word).trim().length > CHARACTER_LIMIT) {
            if (wordPart.trim()) {
              parts.push(wordPart.trim());
            }
            wordPart = word;
          } else {
            wordPart = (wordPart + ' ' + word).trim();
          }
        }

        if (wordPart.trim()) {
          currentPart = wordPart.trim();
        } else {
          currentPart = '';
        }
      } else {
        currentPart = trimmedSentence;
      }
    } else {
      currentPart = (currentPart + ' ' + trimmedSentence).trim();
    }
  }

  // Don't forget the last part
  if (currentPart.trim()) {
    parts.push(currentPart.trim());
  }

  return parts.length > 0 ? parts : [text.slice(0, CHARACTER_LIMIT)];
};

interface ExtendedTargetTextProps extends TargetTextProps {
  currentPart?: number;
  totalParts?: number;
  onPartChange?: (partIndex: number) => void;
  onPartsCalculated?: (parts: string[]) => void;
  onClearResults?: () => void;
  limitReached?: boolean;
}

const TargetText: React.FC<ExtendedTargetTextProps> = ({
  text,
  onTextChange,
  disabled,
  currentPart = 0,
  totalParts = 1,
  onPartChange,
  onPartsCalculated,
  onClearResults,
  limitReached = false,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editValue, setEditValue] = useState<string>(text);
  const [fullText, setFullText] = useState<string>(text);
  const [parts, setParts] = useState<string[]>([text]);
  const [showRewriteOptions, setShowRewriteOptions] = useState<boolean>(false);
  const [isRewriting, setIsRewriting] = useState<boolean>(false);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const [isLoadingIdea, setIsLoadingIdea] = useState<boolean>(false);
  const [selectedVoice, setSelectedVoice] = useState<TTSVoice>('en-GB-SoniaNeural');
  const [isLoadingAudio, setIsLoadingAudio] = useState<boolean>(false);
  const [isPlayingTTS, setIsPlayingTTS] = useState<boolean>(false);
  const [showVoiceSelector, setShowVoiceSelector] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Recalculate parts when fullText changes
  useEffect(() => {
    const newParts = splitTextIntoParts(fullText);
    setParts(newParts);
    onPartsCalculated?.(newParts);
  }, [fullText, onPartsCalculated]);

  const handleEdit = (): void => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(fullText);
  };

  const handleSave = (): void => {
    if (editValue.trim()) {
      const trimmed = editValue.trim();
      setFullText(trimmed);
      const newParts = splitTextIntoParts(trimmed);
      setParts(newParts);
      onTextChange(newParts[0]); // Start with first part
      onPartsCalculated?.(newParts);
      onPartChange?.(0);
    }
    setIsEditing(false);
  };

  const handleCancel = (): void => {
    setEditValue(fullText);
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Escape') {
      handleCancel();
    }
    // Remove Enter to save - allow multiline input
  };

  const handleRewrite = async (style: RewriteStyle): Promise<void> => {
    if (!editValue.trim()) return;

    setIsRewriting(true);
    setRewriteError(null);

    try {
      const result = await rewriteText(editValue.trim(), style);
      setEditValue(result.rewrittenText);
      setShowRewriteOptions(false);
    } catch (error) {
      console.error('Rewrite error:', error);
      setRewriteError('Failed to rewrite text. Please try again.');
    } finally {
      setIsRewriting(false);
    }
  };

  const handleIdeaOfTheDay = async (): Promise<void> => {
    if (disabled || isLoadingIdea) return;

    setIsLoadingIdea(true);
    // Clear previous results when getting new text
    onClearResults?.();
    try {
      const result = await getIdeaOfTheDay();
      const newText = result.text;
      setFullText(newText);
      const newParts = splitTextIntoParts(newText);
      setParts(newParts);
      onTextChange(newParts[0]);
      onPartsCalculated?.(newParts);
      onPartChange?.(0);
    } catch (error) {
      console.error('Failed to get idea of the day:', error);
    } finally {
      setIsLoadingIdea(false);
    }
  };

  const handleListenClick = async (): Promise<void> => {
    // If already playing, stop
    if (isPlayingTTS && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingTTS(false);
      return;
    }

    // Clean up previous audio element
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Create cache key from text + voice
    const cacheKey = `${text.toLowerCase().trim()}:${selectedVoice}`;

    // Check cache first
    const cachedUrl = ttsAudioCache.get(cacheKey);
    if (cachedUrl) {
      const audio = new Audio(cachedUrl);
      audioRef.current = audio;

      audio.onended = () => setIsPlayingTTS(false);
      audio.onerror = () => setIsPlayingTTS(false);

      await audio.play();
      setIsPlayingTTS(true);
      return;
    }

    // Not in cache, fetch from API
    setIsLoadingAudio(true);
    try {
      const audioBlob = await synthesizeSpeech(text, selectedVoice);
      const audioUrl = URL.createObjectURL(audioBlob);

      // Store in cache
      ttsAudioCache.set(cacheKey, audioUrl);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => setIsPlayingTTS(false);
      audio.onerror = () => setIsPlayingTTS(false);

      await audio.play();
      setIsPlayingTTS(true);
    } catch (error) {
      console.error('Failed to play TTS audio:', error);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Cleanup audio on unmount (don't revoke URLs as they're cached)
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const handlePartNavigation = (direction: 'prev' | 'next'): void => {
    if (!onPartChange) return;

    const newIndex = direction === 'next' ? currentPart + 1 : currentPart - 1;
    if (newIndex >= 0 && newIndex < parts.length) {
      onPartChange(newIndex);
      onTextChange(parts[newIndex]);
    }
  };

  const charCount = editValue.length;
  const isOverLimit = charCount > CHARACTER_LIMIT;
  const willSplit = charCount > CHARACTER_LIMIT;

  return (
    <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {parts.length > 1 ? (
            <span>
              Read this text aloud
              <span className="ml-2 text-blue-600 font-bold">
                (Part {currentPart + 1} of {parts.length})
              </span>
            </span>
          ) : (
            'Read this sentence aloud:'
          )}
        </h2>
        {!isEditing && !disabled && !limitReached && (
          <div className="flex items-center gap-3">
            <button
              onClick={handleIdeaOfTheDay}
              disabled={isLoadingIdea}
              className="text-sm text-amber-500 hover:text-amber-600 flex items-center gap-1 transition-colors"
              title="Get an inspiring text for today's practice"
            >
              {isLoadingIdea ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              )}
              Inspire me
            </button>
            <button
              onClick={handleEdit}
              className="text-sm text-blue-500 hover:text-blue-700 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={editValue}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`w-full p-3 text-xl font-medium text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border-2 rounded-lg focus:outline-none resize-none placeholder-gray-400 ${
              isOverLimit ? 'border-yellow-400 focus:border-yellow-500' : 'border-blue-300 dark:border-blue-600 focus:border-blue-500'
            }`}
            rows={4}
            autoFocus
            placeholder="Enter a sentence or paragraph to practice..."
          />

          {/* Character counter and info */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className={charCount > CHARACTER_LIMIT ? 'text-yellow-600' : 'text-gray-500'}>
                {charCount} characters
              </span>
              {willSplit && (
                <span className="text-yellow-600 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Will be split into {splitTextIntoParts(editValue).length} parts
                </span>
              )}
            </div>
            <span className="text-gray-400">
              Limit: {CHARACTER_LIMIT} chars per part
            </span>
          </div>

          {/* AI Rewrite Section */}
          {editValue.trim().length > 0 && (
            <div className="border-t border-gray-200 pt-3">
              {!showRewriteOptions ? (
                <button
                  onClick={() => setShowRewriteOptions(true)}
                  disabled={isRewriting}
                  className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-800 font-medium transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Rewrite with AI
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Choose style:</span>
                    <button
                      onClick={() => setShowRewriteOptions(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {REWRITE_STYLES.map((style) => (
                      <button
                        key={style.value}
                        onClick={() => handleRewrite(style.value)}
                        disabled={isRewriting}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          isRewriting
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                        }`}
                      >
                        <div className="font-semibold">{style.label}</div>
                        <div className="text-xs opacity-75">{style.description}</div>
                      </button>
                    ))}
                  </div>
                  {isRewriting && (
                    <div className="flex items-center justify-center gap-2 text-sm text-purple-600">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Rewriting...
                    </div>
                  )}
                  {rewriteError && (
                    <div className="text-sm text-red-600">{rewriteError}</div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-2xl font-medium text-gray-800 dark:text-gray-100 leading-relaxed">
            "{text}"
          </p>

          {/* Listen button with voice selector */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleListenClick}
              disabled={isLoadingAudio || disabled || limitReached}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isPlayingTTS
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                  : 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/70 border border-blue-200 dark:border-blue-700'
              } ${(isLoadingAudio || disabled || limitReached) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isLoadingAudio ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : isPlayingTTS ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
              {isPlayingTTS ? 'Stop' : 'Listen'}
            </button>

            {/* Voice selector */}
            <div className="relative">
              <button
                onClick={() => setShowVoiceSelector(!showVoiceSelector)}
                disabled={isPlayingTTS || disabled || limitReached}
                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
                  (isPlayingTTS || disabled || limitReached)
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-600'
                }`}
              >
                <span className="hidden sm:inline">
                  {TTS_VOICES.find(v => v.id === selectedVoice)?.name} ({TTS_VOICES.find(v => v.id === selectedVoice)?.accent})
                </span>
                <span className="sm:hidden">
                  {TTS_VOICES.find(v => v.id === selectedVoice)?.accent}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showVoiceSelector && (
                <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10 min-w-[200px]">
                  {TTS_VOICES.map((voice) => (
                    <button
                      key={voice.id}
                      onClick={() => {
                        setSelectedVoice(voice.id);
                        setShowVoiceSelector(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/50 transition-colors ${
                        selectedVoice === voice.id ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <span className="font-medium">{voice.name}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">({voice.accent} {voice.gender})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Part navigation */}
          {parts.length > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => handlePartNavigation('prev')}
                disabled={currentPart === 0 || disabled}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPart === 0 || disabled
                    ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                    : 'text-blue-600 bg-blue-100 hover:bg-blue-200'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              {/* Part indicators */}
              <div className="flex gap-2">
                {parts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (!disabled && onPartChange) {
                        onPartChange(index);
                        onTextChange(parts[index]);
                      }
                    }}
                    disabled={disabled}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      index === currentPart
                        ? 'bg-blue-600 text-white'
                        : disabled
                        ? 'bg-gray-200 text-gray-400'
                        : 'bg-gray-200 text-gray-600 hover:bg-blue-100'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePartNavigation('next')}
                disabled={currentPart === parts.length - 1 || disabled}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  currentPart === parts.length - 1 || disabled
                    ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                    : 'text-blue-600 bg-blue-100 hover:bg-blue-200'
                }`}
              >
                Next
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TargetText;
